import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ITEMS, SECTIONS, dishImg } from "../data/menu";
import { ComboCard, ProductCard } from "./ProductCard";
import { Leaf } from "./Decor";

const PILLS = [
  { id: "all", label: "All" },
  ...SECTIONS.map((s) => ({ id: s.id, label: s.pill })),
];

const sectionImg = (sectionId, groupId) => {
  const s = SECTIONS.find((x) => x.id === sectionId);
  if (s?.groups) return s.groups.find((g) => g.id === groupId)?.img || s.img;
  return s?.img;
};

export const MenuSection = ({ onAdd }) => {
  const [active, setActive] = useState("all");
  const visible = SECTIONS.filter((s) => active === "all" || s.id === active);

  return (
    <section id="menu" className="relative bg-[#FFFDF8] py-16 sm:py-24" data-testid="menu-section">
      <Leaf className="absolute right-2 top-10 w-12 h-12 opacity-60 hidden sm:block" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#3A6B4C] mb-3">Order Food</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#3D2314]">
            Our Homemade Menu
          </h2>
          <p className="mt-3 text-[#8A7364] text-sm sm:text-base">
            Pick your favourites, choose quantities and send your order on WhatsApp — it&rsquo;s that simple.
          </p>
        </motion.div>

        <div className="sticky top-[72px] z-30 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mt-8 bg-[#FFFDF8]/95 backdrop-blur-md">
          <div className="flex gap-2 overflow-x-auto pb-1 justify-start sm:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {PILLS.map((p) => (
              <button
                key={p.id}
                data-testid={`menu-category-pill-${p.id}`}
                onClick={() => setActive(p.id)}
                className={`shrink-0 rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold border transition-all duration-300 ${
                  active === p.id
                    ? "bg-[#3D2314] border-[#3D2314] text-[#FFFDF8] shadow-md"
                    : "bg-white border-[#E8D8C8] text-[#5A4232] hover:border-[#D86A7A] hover:text-[#D86A7A]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="mt-6 space-y-16"
          >
            {visible.map((section) => (
              <div key={section.id} data-testid={`menu-section-${section.id}`}>
                {section.id === "combo" ? (
                  <>
                    <SectionHeading title={section.title} subtitle={section.subtitle} />
                    <ComboCard item={ITEMS.find((i) => i.type === "combo")} img={dishImg(ITEMS.find((i) => i.type === "combo"), section.img)} onAdd={onAdd} />
                  </>
                ) : section.groups ? (
                  <>
                    <SectionHeading title={section.title} subtitle={section.subtitle} />
                    <div className="space-y-12">
                      {section.groups.map((g) => (
                        <div key={g.id}>
                          <h3 className="font-display text-lg sm:text-xl font-medium text-[#3D2314] mb-5 flex items-center gap-3">
                            <span className="w-8 h-[2px] bg-[#D86A7A] inline-block" />
                            {g.title}
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                            {ITEMS.filter((i) => i.section === g.id).map((item) => (
                              <ProductCard key={item.id} item={item} img={dishImg(item, g.img)} onAdd={onAdd} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <SectionHeading title={section.title} subtitle={section.subtitle} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                      {ITEMS.filter((i) => i.section === section.id).map((item) => (
                        <ProductCard key={item.id} item={item} img={dishImg(item, section.img)} onAdd={onAdd} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const SectionHeading = ({ title, subtitle }) => (
  <div className="mb-7">
    <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-[#3D2314] flex items-center gap-3">
      {title}
      <span className="flex-1 h-px bg-[#EFE6DC]" />
    </h3>
    {subtitle && <p className="text-sm text-[#8A7364] mt-1.5">{subtitle}</p>}
  </div>
);

export { sectionImg };
